import React, { useEffect, useMemo, useState } from 'react';
import { Row } from 'react-bootstrap';
import cx from 'classnames';
import { find, flatMap, forEach, get, isEmpty, map, omit, pickBy, round, size, some, times, toNumber, trim } from 'lodash';
import { useLocation, useNavigate } from 'react-router-dom';

import withErrorBoundary from 'src/components/errorBoundary/withErrorBoundary';
import { SearchBar } from 'src/components/input';
import SwitchButton from 'src/components/switchButton';
import { Deal, DealsRequestPayload, } from 'src/types/dealInsights';
import TabPanel from '../components/tabPanel';
import TableHeader from './components/tableHeader';
import TableCell from './components/tableCell';
import Actions from 'src/components/actionbar';
import InfiniteScroll from 'src/components/infiniteScroll';
import PlaceholderCell from './components/placeholderCell';
import { useDealFilterOptions, useDealInsightsSummary, useDealsList } from 'src/query/dealInsights/dealInsights';
import FiltersSlider from './components/filtersSlider';
import styles from './styles.module.scss';
import moment from 'moment';
import DealActivity from '../components/dealActivity';
import { useSelector } from 'react-redux';
import { BulkFiltersDefaultValues, inSightsLabel, dealInsightsTabs, dealTableHeaders } from 'src/utils/dealInsights.helper';
import buildUrl from 'build-url-ts';
import { formatCurrencyToUSD } from 'src/utils/helper';

const intialRequestFilters: DealsRequestPayload = {
    page: 1,
    size: 20,
}

const emptyDeals = '/images/dealInsights/emptyDeals.svg';
const emptyInsights = '/images/dealInsights/noStatis.svg';

const NoResultsFound = ({ msg,src }: { msg: string,src: string }) => (
    <div className={styles.emptyState}>
        <img src={src} alt="No results" className={styles.emptyStateImg} />
        <p className={styles.emptyStateMsg}>{msg}</p>
    </div>
);

const RowPlaceholder = () => {
    return (<Row className={styles.tableRow}>
        {map(dealTableHeaders, (header) => (
            <PlaceholderCell
                key={header.label}
                col={header.width}
                type={header.value}
            />
        ))}
    </Row>)
};

const InsightPlaceholder = () => {
    return (<div className={styles.insightPlacholder}>
         <div className={cx(styles.placeholder, styles.header)}>
               <span className={cx(styles.line, styles.block)} />
         </div>
         <div className={cx(styles.placeholder,  styles.desc)}>
               <span className={cx(styles.line, styles.block)} />
         </div>
    </div>)
};

interface FilterPillsProps {
    filters: any;
    onClear?: () => void;
    onRemove?: (key: string, value?: any) => void;
}

const FilterPills = ({ filters, onClear, onRemove }: FilterPillsProps) => {

    const renderPill = (label: string, key: string,) => (
        <div className={styles.pill}>
            <p>{label}</p>
            {onRemove && (<i className={'paperflite-close'} onClick={() => onRemove(key)}></i>)}
        </div>
    );

    return (
        <div className={styles.filtersPillsContainer}>
            <div className={styles.filters}>
                {/* PIPELINE */}
                {filters.pipelineExternalId && (
                    renderPill(filters.pipelineExternalId.label, 'pipelineExternalId')
                )}

                {/* STAGE */}
                {filters.stageExternalId &&
                    renderPill(filters.stageExternalId.label, 'stageExternalId')
                }

                {/* OWNER */}
                {filters.ownerExternalId &&
                    renderPill(filters.ownerExternalId.name, 'ownerExternalId')
                }

                {/* AMOUNT */}
                {(filters.minAmount || filters.maxAmount) &&
                    renderPill(
                        `$${filters.minAmount || 0} - $${filters.maxAmount || 0}`,
                        'amount'
                    )}


                {/* CLOSE DATE */}
                {(filters.closeDateFrom || filters.closeDateTo) &&
                    renderPill(
                        `${moment(filters.closeDateFrom).format("DD MMM'YY") ?? ''} - ${moment(filters.closeDateTo).format("DD MMM'YY")}`,
                        'closeDate'
                    )}

                {/* LAST ACTIVITY */}
                {(filters.lastActivityFrom || filters.lastActivityTo) &&
                    renderPill(
                        `${moment(filters.lastActivityFrom).format("DD MMM'YY")} - ${moment(filters.lastActivityTo).format("DD MMM'YY")}`,
                        'lastActivity'
                    )}

                {/* CREATED DATE */}
                {(filters.createdDateFrom || filters.createdDateTo) &&
                    renderPill(
                        `${moment(filters.createdDateFrom).format("DD MMM'YY")} - ${moment(filters.createdDateTo).format("DD MMM'YY")}`,
                        'createdDate'
                    )}

            </div>

            {/* RIGHT SIDE */}
            {onClear && <button className={styles.clearAll} onClick={onClear}>
                Clear All
            </button>
            }
        </div>
    );
};

export const DealInsightsDashboard = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const searchParams = new URLSearchParams(location.search);

    // ui 
    const selectedTab = searchParams.get('riskStatus') || dealInsightsTabs[0].value;
    const searchQuery = searchParams.get('search') || '';
    const [showSliderFilter, setShowSliderFilter] = useState(false); // slider filter toggle
    const [showMyDeals, setShowMyDeals] = useState(false); // toggle for only user
    const [bulkFiltersLabels, setBulkFiltersLabels] = useState<Record<string, any>>(BulkFiltersDefaultValues);

    const user = useSelector((state: any) => get(state, 'users.me')); // user

    // request state's
    const [requestDealFilters, setRequestDealFilters] = useState<DealsRequestPayload>(intialRequestFilters); // query Filter Request

    // query's
    const { data: dealRecords, hasNextPage: hasNextPageEvents, loadMore: loadMoreEvents, isFetching: isFetchingRecords, isFetchingNextPage: isFetchingNextPageEvents, refetch: allRecordsFetch, isRefetching: isRefetchingDealRecords, isLoading: isLoadingDealRecords, } = useDealsList(requestDealFilters);
    const { data: dealSummary, isLoading: isLoadingDealSummary } = useDealInsightsSummary(); // summary / insights
    const { data: filterOptions, isLoading: isLoadingFilterOptions } = useDealFilterOptions();// fetch the filter options.

    // Methods
    const listOfDeals = useMemo(() => { // list of deals new deals are appended only when data is fetched
        return flatMap(dealRecords?.pages || [], (page) => page.deals);
    }, [dealRecords]);

    useEffect(() => {
        const searchParams = new URLSearchParams(location.search);
        const isEmpty = !searchParams.toString();

        if (isEmpty) { // initial if empty then set to default value
            const updatedQuery = {
                'riskStatus': dealInsightsTabs[0].value,
            };
            const url = buildUrl(location.pathname, { queryParams: updatedQuery });
            navigate(url);
        }
    }, [])

    useEffect(() => { // bulk filter label render
        if (!filterOptions || !location.search) return;

        const searchParams = new URLSearchParams(location.search);
        const allParams = Object.fromEntries(searchParams.entries());
        // bulk filters
        const bulkFilterKeys = Object.keys(omit(BulkFiltersDefaultValues, ['riskStatus']));
        const appliedBulkFilters = pickBy(allParams, (_, key) => bulkFilterKeys.includes(key));

        if (!isEmpty(appliedBulkFilters)) {
            setBulkFiltersLabels(() => {
                const filters = { ...appliedBulkFilters };
                if (filterOptions) {
                    // pipeline
                    if (appliedBulkFilters?.pipelineExternalId) {
                        const selectedPipeline = find(filterOptions?.pipelines, (pipeline) => pipeline.externalId === appliedBulkFilters?.pipelineExternalId);
                        filters['pipelineExternalId'] = selectedPipeline;

                        if (appliedBulkFilters?.stageExternalId) {
                            const stages = selectedPipeline?.stages;
                            filters['stageExternalId'] = find(stages, (stage) => stage.externalId === appliedBulkFilters?.stageExternalId);
                        }
                    }
                    // owner
                    if (appliedBulkFilters?.ownerExternalId) {
                        filters['ownerExternalId'] = find(filterOptions?.owners, (user) => user.externalId === appliedBulkFilters?.ownerExternalId);
                    }
                }
                return filters;
            })
        } else {
            setBulkFiltersLabels({});
        }

    }, [location.search, filterOptions]);


    useEffect(() => {
        const searchParams = new URLSearchParams(location.search);
        const allParams = Object.fromEntries(searchParams.entries());
        
        if (size(allParams) > 0) {
            const { riskStatus, ...rest } = allParams;
            setRequestDealFilters(() => ({
                ...rest,
                ...(rest?.minAmount && { 'minAmount': toNumber(rest.minAmount) }),
                ...(rest?.maxAmount && { 'maxAmount': toNumber(rest.maxAmount) }),
                ...(riskStatus && riskStatus !== dealInsightsTabs[0].value && { 'riskStatus': riskStatus }),
                page: 1,
                size: 20,
            }));

        } else {
            setRequestDealFilters(() => ({
                page: 1,
                size: 20,
            }));
        }
    }, [location.search])

    // Methods
    // query parms
    const updateQueryParams = (key: string, value: any) => { // add query params
        const searchParams = new URLSearchParams(location.search);
        const existingParams = Object.fromEntries(searchParams.entries());
        const updatedQuery = {
            ...existingParams,
            [key]: value
        };
        const url = buildUrl(location.pathname, { queryParams: updatedQuery });
        navigate(url);
    }

    const removeQueryParams = (keys: string | string[]) => { // remove query params
        const searchParams = new URLSearchParams(location.search);
        forEach(Array.isArray(keys) ? keys : [keys], (key) => {
            searchParams.delete(key); // remove
        });
        const queryString = searchParams.toString();
        const url = queryString ? `${location.pathname}?${queryString}` : location.pathname;
        navigate(url);
    }

    const checkBulkFiltersHasValues = (filters: Record<string, any>) => some(omit(filters, ['riskStatus', 'hasDealRoom']),
        (value) => value !== null && value !== undefined && value !== ''
    );

    const handleTabChange = (tab: string) => {
        if (tab === selectedTab) return;
        updateQueryParams('riskStatus', tab);
    }

    const handleSearchQuery = (query: string | null) => {
        if (query && size(trim(query)) > 0) {
            updateQueryParams('search', query);
        } else {
            removeQueryParams('search');
        }
    }

    const handleMyDealsToggle = () => {
        if (showMyDeals) { // remove owner filter
            removeQueryParams('ownerExternalId');
        } else { // add owner filter
            const userId = get(user, 'id');
            updateQueryParams('ownerExternalId', userId);
        }

        setShowMyDeals(!showMyDeals); // toggle
    };

    const handleRemoveBulkFilters = (key: string) => { // remove particular bulk filter
        if (key === 'pipelineExternalId') { // pipelineExternalId
            removeQueryParams(['pipelineExternalId', 'stageExternalId']);
        } else if (key === 'closeDate') { // closeDate
            removeQueryParams(['closeDateFrom', 'closeDateTo']);
        } else if (key === 'lastActivity') { // lastActivity
            removeQueryParams(['lastActivityFrom', 'lastActivityTo']);
        } else if (key === 'createdDate') { // createdDate
            removeQueryParams(['createdDateFrom', 'createdDateTo']);
        } else if (key === 'amount') { // amount
            removeQueryParams(['minAmount', 'maxAmount']);
        } else {
            removeQueryParams(key);
        }
    };

    const handleClearBulkFilters = () => { // clear bulk filter
        const searchParams = new URLSearchParams(location.search);
        const bulkFilterKeys = Object.keys(omit(BulkFiltersDefaultValues, ['riskStatus']));
        bulkFilterKeys.forEach((key) => {
            searchParams.delete(key); // remove key in route
        });
        const queryString = searchParams.toString();
        const url = queryString ? `${location.pathname}?${queryString}` : location.pathname;
        navigate(url);
    }

    // Render
    return (
        <div className={cx(styles.dashboardContainer)}>
            <div className={styles.contentContainer}>
                {/* LEFT GRID: Primary Workspace (Tabs, Table) */}
                <div className={styles.leftPane}>
                    <div className={styles.header}>
                        <h6 className={styles.title}>deal insights</h6>
                        <p className={styles.subText}>a snapshot of all your deals in one place</p>
                    </div>

                    <div className={styles.tabFilterWrapper}>
                        <TabPanel
                            listOfTabs={dealInsightsTabs}
                            selected={selectedTab}
                            onChange={(val) => handleTabChange(val)}
                        />

                        <Actions.Button
                            width={2}
                            className={styles.filterButton}
                            onClick={() => setShowSliderFilter(!showSliderFilter)}
                            title="Filters"
                            icon={cx('paperflite-dropdown', styles.icon)}
                        />
                    </div>

                    {/* Fitler Pills */}
                    {checkBulkFiltersHasValues(bulkFiltersLabels) && (
                        <FilterPills
                            filters={bulkFiltersLabels}
                            onRemove={handleRemoveBulkFilters}
                            onClear={handleClearBulkFilters}
                        />
                    )}

                    {/* Deals Wrapper: search + toggle controls above the deal table */}
                    <div className={styles.dealsWrapper}>
                        <div className={styles.dealsControls}>
                            <div className={styles.searchBar}>
                                <SearchBar
                                    query={searchQuery}
                                    onSearch={(query: string | null) => handleSearchQuery(query)}
                                />
                            </div>
                            <div className={cx(styles.mydeals)}>
                                <SwitchButton
                                    id="show-my-deals"
                                    checked={showMyDeals}
                                    handleCheck={handleMyDealsToggle}
                                    label="Show only my deals"
                                    className={styles.switchToggle}
                                />
                            </div>
                        </div>

                        {/* Deal Table */}
                        <div className={styles.tableWrapper}>
                            <Row className={cx(styles.tableHeaderRow)}>
                                {map(dealTableHeaders, (header) => (
                                    <TableHeader
                                        key={header.label}
                                        col={header.width}
                                        value={header.label}
                                        onClick={() => { }}
                                    />
                                ))}
                            </Row>
                            <div className={styles.tableBody}>
                                {isLoadingDealRecords || isRefetchingDealRecords ? <div className={styles.tableBodyWrapper}>
                                    {times(4, (index) => (
                                        <RowPlaceholder key={index} />
                                    ))} </div>
                                    : <>{size(listOfDeals) > 0 ? (
                                        <InfiniteScroll
                                            loading={isFetchingRecords}
                                            hasMore={hasNextPageEvents}
                                            loadMore={loadMoreEvents}
                                            loader={() => ( 
                                                <> 
                                                    <RowPlaceholder />
                                                    <RowPlaceholder />
                                                </>
                                            )}
                                            className={styles.tableBodyWrapper}
                                        >
                                            {map(listOfDeals, (deal: Deal, index) => <Row
                                                key={deal.id}
                                                className={cx(styles.tableRow)}
                                                onClick={() => navigate(`/deal-insights/${deal.id}`)}
                                            >
                                                <TableCell col={dealTableHeaders[0].width} type="name" value={deal.dealName} classnames={styles.name} />
                                                <TableCell col={dealTableHeaders[1].width} type="amount" value={deal.amount} />
                                                {/* <TableCell col={dealTableHeaders[2].width} type="prediction" value={deal.predictionScore} predictionTrend={deal.predictionTrend} /> */}
                                                <TableCell col={dealTableHeaders[2].width} type="owner" value={deal.ownerName} />
                                                <TableCell col={dealTableHeaders[3].width} type="lastActivity" value={deal.lastActivity} classnames={styles.arrow} showArrow={true} />
                                            </Row>)}
                                        </InfiniteScroll>
                                    ) : (
                                        <div className={styles.noDeals}>
                                            <NoResultsFound msg="No results found!" src={emptyDeals} />
                                        </div>
                                    )} </>
                                }
                            </div>
                        </div>

                        {/* Bottom fade overlay */}
                        <div className={styles.bottomOverlay} />
                    </div>
                </div>

                {/* RIGHT GRID: AI Insights & Metrics */}
                <div className={styles.rightPane}>
                    <div className={styles.content}>
                    
                    {/* <DealAgent classnames={styles.dealAgent} enableClearChat={true} /> */}

                    {/* Recent Activities */}
                    <DealActivity isDetailedView={false} classnames={styles.recentActivities} title={'recent activities'} />

                    {/* Insights */}
                    <div className={styles.insights}>
                        <p className={styles.title}>insights</p>
                        <div className={styles.content}> 
                            { isLoadingDealSummary ?
                                <>
                                   {times(4, (index) => (
                                    <InsightPlaceholder key={index} />
                                   ))}
                                </>
                                : <>
                                    {size(dealSummary) > 0 ?
                                        <>
                                            {map(dealSummary, (value: number, key: string) => {
                                                return <div className={styles.item}>
                                                    <p className={styles.title}>{inSightsLabel[key]}</p>
                                                    <p className={styles.value}> {
                                                      (key === 'thisMonthActivityChangePct') 
                                                       ? '-' : (key === 'valueByRevenue' || key === 'avgDealSize') ? formatCurrencyToUSD(value) : `${round(value)}` }</p>
                                                </div>
                                            })}
                                        </>
                                        : <div className={styles.noInsights}>
                                            <NoResultsFound msg="No insights found!" src={emptyInsights} />
                                        </div>
                                    }
                                </>
                            }
                        </div>
                    </div>
                    </div>
                </div>
            </div>

            {/* Filter Slider */}
            <FiltersSlider
                isOpen={showSliderFilter}
                onClose={() => setShowSliderFilter(false)}
                filterOptions={filterOptions} />
        </div>
    );
};

export default withErrorBoundary(DealInsightsDashboard);

